import { useCallback, useEffect, useMemo, useRef, useState, type ChangeEvent } from "react";
import {
  Alert,
  Badge,
  Box,
  Button,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  FormControl,
  InputLabel,
  IconButton,
  MenuItem,
  Select,
  Snackbar,
  Stack,
  ToggleButton,
  Tooltip,
  Typography
} from "@mui/material";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import EditNoteIcon from "@mui/icons-material/EditNote";
import AddBoxIcon from "@mui/icons-material/AddBox";
import EditIcon from "@mui/icons-material/Edit";
import DeleteIcon from "@mui/icons-material/Delete";
import CloudUploadIcon from "@mui/icons-material/CloudUpload";
import FileDownloadIcon from "@mui/icons-material/FileDownload";
import FileUploadIcon from "@mui/icons-material/FileUpload";
import FilterAltIcon from "@mui/icons-material/FilterAlt";
import { useAuth } from "../context/AuthContext";
import { useAppContext } from "../context/AppContext";
import type { UserGroupSummary } from "../types/user";
import type { AnnotationStorage, AnnotationSummary, AnnotationUpsertInput } from "../types/annotation";
import AnnotationAreaFilterDialog, { type AnnotationAreaFilterBBox } from "../components/AnnotationAreaFilterDialog";
import AnnotationDialog from "../components/AnnotationDialog";
import AnnotationTable from "../components/AnnotationTable";

type SnackbarState = {
  open: boolean;
  message: string;
  severity: "success" | "error";
};

const createDefaultSnackbarState = (): SnackbarState => ({
  open: false,
  message: "",
  severity: "success"
});

type AnnotationResponse = {
  id?: string;
  name?: string;
  description?: string;
  storage?: string;
  active?: boolean;
  tags?: string[];
  userGroups?: string[];
  featureCollection?: unknown;
  bbox?: number[];
  restricted?: boolean;
};

type GeoJSONObject = Record<string, unknown>;

type ImportedAnnotationMetadata = {
  name: string;
  description: string;
  storage: AnnotationStorage;
  active: boolean;
  tags: string[];
  userGroups: string[];
  bbox: number[];
  restricted: boolean;
};

const GEOJSON_FILE_PATTERN = /\.(geojson|gejson|json)$/iu;
const LOCAL_ANNOTATIONS_STORAGE_KEY = "sky-view.annotations.local";
const ANNOTATION_AREA_FILTER_STORAGE_KEY = "sky-view.annotations.areaFilter.bbox";
const RESTRICTED_ADMIN_ONLY_MESSAGE = "Restricted annotations can only be modified or deleted by administrators.";
const IMPORT_RESTRICTED_NORMALIZED_MESSAGE = "Restricted metadata in imported files is ignored for non-admin imports.";

const isRecord = (value: unknown): value is Record<string, unknown> =>
  Boolean(value) && typeof value === "object" && !Array.isArray(value);

const toStringValue = (value: unknown): string | null => {
  if (typeof value !== "string") {
    return null;
  }

  const trimmed = value.trim();
  return trimmed ? trimmed : null;
};

const toBooleanValue = (value: unknown): boolean | null => {
  if (typeof value === "boolean") {
    return value;
  }
  if (typeof value === "string") {
    const normalized = value.trim().toLowerCase();
    if (normalized === "true") {
      return true;
    }
    if (normalized === "false") {
      return false;
    }
  }
  return null;
};

const isAnnotationStorage = (value: unknown): value is AnnotationStorage =>
  value === "server" || value === "local";

const sortAnnotations = (annotations: AnnotationSummary[]): AnnotationSummary[] =>
  [...annotations].sort((left, right) => left.name.localeCompare(right.name, undefined, { sensitivity: "base" }));

const normalizeStoredLocalAnnotations = (annotations: AnnotationSummary[]): AnnotationSummary[] =>
  sortAnnotations(
    annotations
      .filter((annotation) => annotation.storage === "local")
      .map((annotation) => ({ ...annotation, storage: "local" as const }))
  );

const persistLocalAnnotations = (annotations: AnnotationSummary[]) => {
  if (typeof window === "undefined") {
    return;
  }

  window.localStorage.setItem(LOCAL_ANNOTATIONS_STORAGE_KEY, JSON.stringify(normalizeStoredLocalAnnotations(annotations)));
};

const readLocalAnnotations = (): AnnotationSummary[] => {
  if (typeof window === "undefined") {
    return [];
  }

  const raw = window.localStorage.getItem(LOCAL_ANNOTATIONS_STORAGE_KEY);
  if (!raw) {
    return [];
  }

  try {
    const parsed = JSON.parse(raw) as AnnotationResponse[];
    if (!Array.isArray(parsed)) {
      return [];
    }

    return normalizeStoredLocalAnnotations(
      parsed
        .map((annotation) => normalizeAnnotation(annotation, "local"))
        .filter((annotation): annotation is AnnotationSummary => Boolean(annotation))
    );
  } catch {
    return [];
  }
};

const createLocalAnnotationId = (): string => {
  if (typeof globalThis !== "undefined" && typeof globalThis.crypto?.randomUUID === "function") {
    return `local-${globalThis.crypto.randomUUID()}`;
  }

  return `local-${Date.now()}-${Math.random().toString(16).slice(2)}`;
};

const toStringList = (value: unknown): string[] => {
  if (Array.isArray(value)) {
    return Array.from(
      new Set(
        value
          .map((entry) => (typeof entry === "string" ? entry.trim() : ""))
          .filter((entry) => entry.length > 0)
      )
    );
  }

  if (typeof value === "string") {
    return Array.from(
      new Set(
        value
          .split(",")
          .map((entry) => entry.trim())
          .filter((entry) => entry.length > 0)
      )
    );
  }

  return [];
};

const extractBBoxFromCoordinates = (coordinates: unknown, acc: [number, number, number, number] | null): [number, number, number, number] | null => {
  if (!Array.isArray(coordinates)) {
    return acc;
  }

  if (
    coordinates.length >= 2 &&
    typeof coordinates[0] === "number" &&
    Number.isFinite(coordinates[0]) &&
    typeof coordinates[1] === "number" &&
    Number.isFinite(coordinates[1])
  ) {
    const lon = coordinates[0];
    const lat = coordinates[1];
    if (!acc) {
      return [lon, lat, lon, lat];
    }
    return [
      Math.min(acc[0], lon),
      Math.min(acc[1], lat),
      Math.max(acc[2], lon),
      Math.max(acc[3], lat)
    ];
  }

  let next = acc;
  for (const entry of coordinates) {
    next = extractBBoxFromCoordinates(entry, next);
  }
  return next;
};

const computeGeoJSONBBox = (value: unknown): number[] => {
  if (!isRecord(value)) {
    return [];
  }

  const rawBBox = value.bbox;
  if (Array.isArray(rawBBox) && rawBBox.length === 4 && rawBBox.every((entry) => typeof entry === "number" && Number.isFinite(entry))) {
    return rawBBox;
  }

  const type = value.type;
  if (type === "FeatureCollection") {
    const features = Array.isArray(value.features) ? value.features : [];
    let bbox: [number, number, number, number] | null = null;
    for (const feature of features) {
      bbox = extractBBoxFromCoordinates(isRecord(feature) && isRecord(feature.geometry) ? feature.geometry.coordinates : null, bbox);
    }
    return bbox ? [...bbox] : [];
  }

  if (type === "Feature") {
    const geometry = isRecord(value.geometry) ? value.geometry : null;
    const bbox = extractBBoxFromCoordinates(geometry?.coordinates, null);
    return bbox ? [...bbox] : [];
  }

  const bbox = extractBBoxFromCoordinates(value.coordinates, null);
  return bbox ? [...bbox] : [];
};

const normalizeBBox = (value: unknown): AnnotationAreaFilterBBox | null => {
  if (
    !Array.isArray(value) ||
    value.length !== 4 ||
    value.some((entry) => typeof entry !== "number" || !Number.isFinite(entry))
  ) {
    return null;
  }

  const [rawWest, rawSouth, rawEast, rawNorth] = value;
  return [
    Math.min(rawWest, rawEast),
    Math.min(rawSouth, rawNorth),
    Math.max(rawWest, rawEast),
    Math.max(rawSouth, rawNorth)
  ];
};

const readStoredAnnotationAreaFilterBBox = (): AnnotationAreaFilterBBox | null => {
  if (typeof window === "undefined") {
    return null;
  }

  try {
    const raw = window.localStorage.getItem(ANNOTATION_AREA_FILTER_STORAGE_KEY);
    if (!raw) {
      return null;
    }

    return normalizeBBox(JSON.parse(raw) as unknown);
  } catch {
    return null;
  }
};

const persistAnnotationAreaFilterBBox = (bbox: AnnotationAreaFilterBBox) => {
  if (typeof window === "undefined") {
    return;
  }

  try {
    window.localStorage.setItem(ANNOTATION_AREA_FILTER_STORAGE_KEY, JSON.stringify(bbox));
  } catch {
    // ignore storage failures
  }
};

const annotationBBoxIntersects = (
  left: AnnotationAreaFilterBBox,
  right: AnnotationAreaFilterBBox
): boolean => left[0] <= right[2] && left[2] >= right[0] && left[1] <= right[3] && left[3] >= right[1];

const getAnnotationBBox = (annotation: AnnotationSummary): AnnotationAreaFilterBBox | null => {
  const normalized = normalizeBBox(annotation.bbox);
  if (normalized) {
    return normalized;
  }

  return normalizeBBox(computeGeoJSONBBox(annotation.featureCollection));
};

const getGeoJSONSources = (featureCollection: GeoJSONObject): Record<string, unknown>[] => {
  const sources: Record<string, unknown>[] = [featureCollection];

  if (isRecord(featureCollection.properties)) {
    sources.push(featureCollection.properties);
  }

  const features = Array.isArray(featureCollection.features) ? featureCollection.features : [];
  for (const feature of features) {
    if (isRecord(feature)) {
      sources.push(feature);
      if (isRecord(feature.properties)) {
        sources.push(feature.properties);
      }
    }
  }

  return sources;
};

const getFirstStringProperty = (sources: Record<string, unknown>[], keys: string[]): string | null => {
  for (const source of sources) {
    for (const key of keys) {
      const value = toStringValue(source[key]);
      if (value) {
        return value;
      }
    }
  }
  return null;
};

const getFirstBooleanProperty = (sources: Record<string, unknown>[], keys: string[]): boolean | null => {
  for (const source of sources) {
    for (const key of keys) {
      const value = toBooleanValue(source[key]);
      if (value != null) {
        return value;
      }
    }
  }
  return null;
};

const collectStringListProperty = (sources: Record<string, unknown>[], keys: string[]): string[] => {
  const values = new Set<string>();
  for (const source of sources) {
    for (const key of keys) {
      for (const value of toStringList(source[key])) {
        values.add(value);
      }
    }
  }
  return Array.from(values);
};

const stripFileExtension = (name: string): string => name.replace(/\.(geojson|gejson|json)$/iu, "");

const buildImportedAnnotation = (featureCollection: GeoJSONObject, fileName: string): ImportedAnnotationMetadata => {
  const sources = getGeoJSONSources(featureCollection);
  const derivedName =
    getFirstStringProperty(sources, ["name", "title"]) ?? stripFileExtension(fileName) ?? "Imported annotation";
  const derivedDescription =
    getFirstStringProperty(sources, ["description", "desc", "summary"]) ?? "";
  const derivedTags = collectStringListProperty(sources, ["tags", "tag", "categories", "category"]);
  const derivedUserGroups = collectStringListProperty(sources, ["userGroups", "userGroupIds", "user_group_ids", "groups"]);
  const derivedActive = getFirstBooleanProperty(sources, ["active", "enabled"]) ?? true;
  const derivedRestricted =
    getFirstBooleanProperty(sources, ["restricted", "private"]) ?? derivedUserGroups.length > 0;

  return {
    name: derivedName,
    description: derivedDescription,
    storage: "server",
    active: derivedActive,
    tags: derivedTags,
    userGroups: derivedUserGroups,
    bbox: computeGeoJSONBBox(featureCollection),
    restricted: derivedRestricted
  };
};

const buildExportableFeatureCollection = (annotation: AnnotationSummary): GeoJSONObject => {
  const base = isRecord(annotation.featureCollection)
    ? JSON.parse(JSON.stringify(annotation.featureCollection)) as GeoJSONObject
    : ({ type: "FeatureCollection", features: [] } satisfies GeoJSONObject);

  base.type = "FeatureCollection";
  if (!Array.isArray(base.features)) {
    base.features = [];
  }

  base.name = annotation.name;
  base.title = annotation.name;
  if (annotation.description) {
    base.description = annotation.description;
  }
  base.storage = annotation.storage;
  if (annotation.tags.length > 0) {
    base.tags = [...annotation.tags];
  }
  base.active = annotation.active;
  base.restricted = annotation.restricted;
  if (annotation.userGroups.length > 0) {
    base.userGroups = [...annotation.userGroups];
  }
  if (annotation.bbox.length === 4) {
    base.bbox = [...annotation.bbox];
  }

  return base;
};

const downloadTextFile = (fileName: string, content: string, mimeType: string) => {
  const blob = new Blob([content], { type: mimeType });
  const url = URL.createObjectURL(blob);
  const link = document.createElement("a");
  link.href = url;
  link.download = fileName;
  document.body.appendChild(link);
  link.click();
  document.body.removeChild(link);
  URL.revokeObjectURL(url);
};

const normalizeAnnotation = (
  annotation: AnnotationResponse,
  fallbackStorage: AnnotationStorage
): AnnotationSummary | null => {
  const id = toStringValue(annotation.id);
  const name = toStringValue(annotation.name);

  if (!id || !name) {
    return null;
  }

  return {
    id,
    name,
    description: typeof annotation.description === "string" ? annotation.description : "",
    storage: isAnnotationStorage(annotation.storage) ? annotation.storage : fallbackStorage,
    active: annotation.active !== false,
    tags: toStringList(annotation.tags),
    userGroups: toStringList(annotation.userGroups),
    featureCollection: annotation.featureCollection ?? null,
    bbox: Array.isArray(annotation.bbox)
      ? annotation.bbox.filter((value): value is number => typeof value === "number" && Number.isFinite(value))
      : [],
    restricted: Boolean(annotation.restricted)
  };
};

const AnnotationsPage = () => {
  const { authorizedFetch, user: authUser, userManagementEnabled } = useAuth();
  const { serverConfig } = useAppContext();
  const isUserManagementEnabled = userManagementEnabled && serverConfig.userManagementEnabled;
  const hasAdminRights = !isUserManagementEnabled || authUser?.role === "admin";
  const queryClient = useQueryClient();
  const fileInputRef = useRef<HTMLInputElement | null>(null);

  const [selectedAnnotationIds, setSelectedAnnotationIds] = useState<string[]>([]);
  const [addOpen, setAddOpen] = useState(false);
  const [areaFilterDialogOpen, setAreaFilterDialogOpen] = useState(false);
  const [annotationAreaFilter, setAnnotationAreaFilter] = useState<AnnotationAreaFilterBBox | null>(null);
  const [lastSelectedAreaFilterBBox, setLastSelectedAreaFilterBBox] = useState<AnnotationAreaFilterBBox | null>(
    () => readStoredAnnotationAreaFilterBBox()
  );
  const [editingAnnotation, setEditingAnnotation] = useState<AnnotationSummary | null>(null);
  const [importDialogOpen, setImportDialogOpen] = useState(false);
  const [selectedImportFiles, setSelectedImportFiles] = useState<File[]>([]);
  const [importTargetStorage, setImportTargetStorage] = useState<AnnotationStorage>(hasAdminRights ? "server" : "local");
  const [importError, setImportError] = useState<string | null>(null);
  const [importDragActive, setImportDragActive] = useState(false);
  const [importingAnnotations, setImportingAnnotations] = useState(false);
  const [deleteTargets, setDeleteTargets] = useState<AnnotationSummary[]>([]);
  const [deletingAnnotation, setDeletingAnnotation] = useState(false);
  const [deleteError, setDeleteError] = useState<string | null>(null);
  const [snackbar, setSnackbar] = useState<SnackbarState>(createDefaultSnackbarState);
  const [localAnnotations, setLocalAnnotations] = useState<AnnotationSummary[]>(() => readLocalAnnotations());

  const {
    data,
    isLoading,
    isError,
    error
  } = useQuery<AnnotationSummary[]>({
    queryKey: ["annotations", annotationAreaFilter?.join(",") ?? "all"],
    queryFn: async () => {
      const params = new URLSearchParams();
      if (annotationAreaFilter) {
        params.set("bbox", annotationAreaFilter.join(","));
      }

      const response = await authorizedFetch(
        params.size > 0 ? `/api/annotations?${params.toString()}` : "/api/annotations"
      );
      const payload = (await response.json().catch(() => null)) as {
        annotations?: AnnotationResponse[];
        error?: string;
      } | null;
      if (!response.ok) {
        throw new Error(payload?.error ?? "Unable to load annotations");
      }
      return (payload?.annotations ?? [])
        .map((annotation) => normalizeAnnotation(annotation, "server"))
        .filter((annotation): annotation is AnnotationSummary => Boolean(annotation));
    }
  });

  const {
    data: userGroupsData,
    isError: isUserGroupsError,
    error: userGroupsError
  } = useQuery<UserGroupSummary[]>({
    queryKey: ["usergroups"],
    queryFn: async () => {
      const response = await authorizedFetch("/api/usergroups");
      const payload = (await response.json().catch(() => null)) as {
        userGroups?: UserGroupSummary[];
        error?: string;
      } | null;
      if (!response.ok) {
        throw new Error(payload?.error ?? "Unable to load user groups");
      }
      return payload?.userGroups ?? [];
    },
    enabled: isUserManagementEnabled && hasAdminRights
  });

  const serverAnnotations = useMemo(() => data ?? [], [data]);

  const filteredLocalAnnotations = useMemo(() => {
    if (!annotationAreaFilter) {
      return localAnnotations;
    }

    return localAnnotations.filter((annotation) => {
      const bbox = getAnnotationBBox(annotation);
      return bbox ? annotationBBoxIntersects(bbox, annotationAreaFilter) : false;
    });
  }, [annotationAreaFilter, localAnnotations]);

  useEffect(() => {
    if (typeof window === "undefined") {
      return;
    }

    const handleStorage = (event: StorageEvent) => {
      if (event.key !== LOCAL_ANNOTATIONS_STORAGE_KEY) {
        return;
      }

      setLocalAnnotations(readLocalAnnotations());
    };

    window.addEventListener("storage", handleStorage);
    return () => {
      window.removeEventListener("storage", handleStorage);
    };
  }, []);

  const updateLocalAnnotations = useCallback((updater: (current: AnnotationSummary[]) => AnnotationSummary[]) => {
    setLocalAnnotations((current) => {
      const next = normalizeStoredLocalAnnotations(updater(current));
      persistLocalAnnotations(next);
      return next;
    });
  }, []);

  const availableUserGroups = useMemo(() => {
    if (!isUserManagementEnabled) {
      return [];
    }

    if (hasAdminRights) {
      return userGroupsData ?? [];
    }

    return Array.from(new Set(authUser?.userGroups ?? [])).map((groupId) => ({ id: groupId, name: groupId }));
  }, [authUser?.userGroups, hasAdminRights, isUserManagementEnabled, userGroupsData]);

  const annotations = useMemo(
    () => sortAnnotations([...serverAnnotations, ...filteredLocalAnnotations]),
    [filteredLocalAnnotations, serverAnnotations]
  );

  const annotationById = useMemo(() => {
    const map = new Map<string, AnnotationSummary>();
    for (const annotation of annotations) {
      map.set(annotation.id, annotation);
    }
    return map;
  }, [annotations]);

  useEffect(() => {
    setSelectedAnnotationIds((prev) => prev.filter((id) => annotationById.has(id)));
  }, [annotationById]);

  const invalidateAnnotations = useCallback(async () => {
    await queryClient.invalidateQueries({ queryKey: ["annotations"] });
  }, [queryClient]);

  const handleAreaFilterSelected = useCallback((bbox: AnnotationAreaFilterBBox) => {
    persistAnnotationAreaFilterBBox(bbox);
    setLastSelectedAreaFilterBBox(bbox);
    setAnnotationAreaFilter(bbox);
    setAreaFilterDialogOpen(false);
  }, []);

  const handleAreaFilterToggle = useCallback(() => {
    if (annotationAreaFilter) {
      setAnnotationAreaFilter(null);
      return;
    }

    setAreaFilterDialogOpen(true);
  }, [annotationAreaFilter]);

  const createServerAnnotation = useCallback(
    async (input: AnnotationUpsertInput) => {
      const response = await authorizedFetch("/api/annotations", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          name: input.name,
          description: input.description,
          storage: input.storage,
          active: input.active,
          tags: input.tags,
          userGroups: input.userGroups,
          restricted: input.restricted,
          ...(input.featureCollection ? { featureCollection: input.featureCollection } : {}),
          ...(input.bbox.length > 0 ? { bbox: input.bbox } : {})
        })
      });

      const payload = (await response.json().catch(() => null)) as { error?: string } | null;
      if (!response.ok) {
        throw new Error(payload?.error ?? "Unable to create annotation");
      }
    },
    [authorizedFetch]
  );

  const updateServerAnnotation = useCallback(
    async (id: string, input: AnnotationUpsertInput) => {
      const response = await authorizedFetch(`/api/annotations/${id}`, {
        method: "PUT",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          name: input.name,
          description: input.description,
          storage: input.storage,
          active: input.active,
          tags: input.tags,
          userGroups: input.userGroups,
          restricted: input.restricted,
          ...(input.featureCollection ? { featureCollection: input.featureCollection } : {}),
          ...(input.bbox.length > 0 ? { bbox: input.bbox } : {})
        })
      });

      const payload = (await response.json().catch(() => null)) as { error?: string } | null;
      if (!response.ok) {
        throw new Error(payload?.error ?? "Unable to update annotation");
      }
    },
    [authorizedFetch]
  );

  const deleteServerAnnotation = useCallback(
    async (id: string) => {
      const response = await authorizedFetch(`/api/annotations/${id}`, {
        method: "DELETE"
      });
      const payload = (await response.json().catch(() => null)) as { error?: string } | null;
      if (!response.ok) {
        throw new Error(payload?.error ?? "Unable to delete annotation");
      }
    },
    [authorizedFetch]
  );

  const closeSnackbar = () => {
    setSnackbar((prev) => (prev.open ? { ...prev, open: false } : prev));
  };

  const selectedAnnotations = useMemo(
    () =>
      selectedAnnotationIds
        .map((id) => annotationById.get(id))
        .filter((value): value is AnnotationSummary => Boolean(value)),
    [annotationById, selectedAnnotationIds]
  );

  const canMutateAnnotation = useCallback(
    (annotation: AnnotationSummary) => hasAdminRights || !annotation.restricted,
    [hasAdminRights]
  );

  const canEditSelected = selectedAnnotations.length === 1 && canMutateAnnotation(selectedAnnotations[0]);
  const canDeleteSelected = selectedAnnotations.length > 0 && selectedAnnotations.every(canMutateAnnotation);
  const canExportSelected = selectedAnnotations.length > 0;

  const handleExportAnnotations = useCallback(() => {
    if (selectedAnnotations.length === 0) {
      return;
    }

    try {
      for (const annotation of selectedAnnotations) {
        const payload = buildExportableFeatureCollection(annotation);
        const safeName = annotation.name.trim().replace(/[^a-z0-9-_]+/giu, "-").replace(/^-+|-+$/gu, "") || "annotation";
        downloadTextFile(`${safeName}.geojson`, JSON.stringify(payload, null, 2), "application/geo+json");
      }

      setSnackbar({
        open: true,
        message:
          selectedAnnotations.length === 1
            ? `${selectedAnnotations[0].name} exported.`
            : `${selectedAnnotations.length} annotations exported.`,
        severity: "success"
      });
    } catch (err) {
      console.error("Export annotations failed", err);
      setSnackbar({ open: true, message: "Failed to export annotations.", severity: "error" });
    }
  }, [selectedAnnotations]);

  const resetImportDialog = useCallback(() => {
    setImportDialogOpen(false);
    setSelectedImportFiles([]);
    setImportTargetStorage(hasAdminRights ? "server" : "local");
    setImportError(null);
    setImportDragActive(false);
    setImportingAnnotations(false);
    if (fileInputRef.current) {
      fileInputRef.current.value = "";
    }
  }, [hasAdminRights]);

  const validateImportFiles = useCallback((files: File[]) => {
    if (files.length === 0) {
      return "Select one or more GeoJSON files.";
    }

    const invalid = files.find((file) => !GEOJSON_FILE_PATTERN.test(file.name));
    if (invalid) {
      return `${invalid.name}: unsupported file type`;
    }

    return null;
  }, []);

  const handleImportSelection = useCallback(
    (files: File[]) => {
      const errorMessage = validateImportFiles(files);
      if (errorMessage) {
        setSelectedImportFiles([]);
        setImportError(errorMessage);
        return;
      }

      setSelectedImportFiles(files);
      setImportError(null);
    },
    [validateImportFiles]
  );

  const handleImportClick = useCallback(() => {
    setImportDialogOpen(true);
    setSelectedImportFiles([]);
    setImportTargetStorage(hasAdminRights ? "server" : "local");
    setImportError(null);
    setImportDragActive(false);
  }, [hasAdminRights]);

  const processImportedFiles = useCallback(
    async (files: File[], targetStorage: AnnotationStorage) => {
      let imported = 0;
      const errors: string[] = [];
      let normalizedRestrictedCount = 0;

      for (const file of files) {
        if (!GEOJSON_FILE_PATTERN.test(file.name)) {
          errors.push(`${file.name}: unsupported file type`);
          continue;
        }

        try {
          const rawText = await file.text();
          const parsed = JSON.parse(rawText) as unknown;
          if (!isRecord(parsed) || parsed.type !== "FeatureCollection") {
            errors.push(`${file.name}: file must contain a GeoJSON FeatureCollection`);
            continue;
          }

          const metadata = buildImportedAnnotation(parsed, file.name);
          const restricted = hasAdminRights ? metadata.restricted : false;
          if (!hasAdminRights && metadata.restricted) {
            normalizedRestrictedCount += 1;
          }
          const input: AnnotationUpsertInput = {
            name: metadata.name,
            description: metadata.description,
            storage: targetStorage,
            active: metadata.active,
            tags: metadata.tags,
            userGroups: metadata.userGroups,
            featureCollection: parsed,
            bbox: metadata.bbox,
            restricted
          };

          if (targetStorage === "server") {
            try {
              await createServerAnnotation(input);
            } catch (err) {
              const message = err instanceof Error ? err.message : "Unable to import annotation";
              errors.push(`${file.name}: ${message}`);
              continue;
            }
          } else {
            updateLocalAnnotations((current) => [
              ...current,
              {
                ...input,
                id: createLocalAnnotationId(),
                storage: "local"
              }
            ]);
          }

          imported += 1;
        } catch (err) {
          const message = err instanceof Error ? err.message : "Invalid file";
          errors.push(`${file.name}: ${message}`);
        }
      }

      if (imported > 0) {
        setSelectedAnnotationIds([]);
        if (hasAdminRights) {
          await invalidateAnnotations();
        }
      }

      if (errors.length > 0) {
        const normalizedNotice = normalizedRestrictedCount > 0 ? ` ${IMPORT_RESTRICTED_NORMALIZED_MESSAGE}` : "";
        setSnackbar({
          open: true,
          message:
            imported > 0
              ? `Imported ${imported} file(s). ${errors.slice(0, 2).join("; ")}${normalizedNotice}`
              : `${errors.slice(0, 2).join("; ")}${normalizedNotice}`,
          severity: imported > 0 ? "success" : "error"
        });
        return;
      }

      const normalizedNotice = normalizedRestrictedCount > 0 ? ` ${IMPORT_RESTRICTED_NORMALIZED_MESSAGE}` : "";
      setSnackbar({
        open: true,
        message: `${imported === 1 ? "1 annotation imported." : `${imported} annotations imported.`}${normalizedNotice}`,
        severity: "success"
      });
    },
    [createServerAnnotation, hasAdminRights, invalidateAnnotations, updateLocalAnnotations]
  );

  const handleImportFileChange = useCallback(
    (event: ChangeEvent<HTMLInputElement>) => {
      const files = Array.from(event.target.files ?? []);
      handleImportSelection(files);
    },
    [handleImportSelection]
  );

  const handleImportDragOver = useCallback((event: React.DragEvent) => {
    event.preventDefault();
    event.dataTransfer.dropEffect = "copy";
    setImportDragActive(true);
  }, []);

  const handleImportDragLeave = useCallback(() => {
    setImportDragActive(false);
  }, []);

  const handleImportDrop = useCallback(
    (event: React.DragEvent) => {
      event.preventDefault();
      setImportDragActive(false);
      const files = Array.from(event.dataTransfer.files ?? []);
      handleImportSelection(files);
    },
    [handleImportSelection]
  );

  const handleConfirmImport = useCallback(async () => {
    if (selectedImportFiles.length === 0) {
      setImportError("Select one or more GeoJSON files.");
      return;
    }

    setImportingAnnotations(true);
    setImportError(null);
    try {
      await processImportedFiles(selectedImportFiles, importTargetStorage);
      resetImportDialog();
    } catch (err) {
      const message = err instanceof Error ? err.message : "Unable to import annotations.";
      setImportError(message);
      setImportingAnnotations(false);
    }
  }, [importTargetStorage, processImportedFiles, resetImportDialog, selectedImportFiles]);

  const importFileSummary = useMemo(() => {
    if (selectedImportFiles.length === 0) {
      return "Drop GeoJSON files here or click to browse";
    }
    if (selectedImportFiles.length === 1) {
      return selectedImportFiles[0].name;
    }
    return `${selectedImportFiles.length} files selected`;
  }, [selectedImportFiles]);

  const importFileDetails = useMemo(() => {
    if (selectedImportFiles.length <= 1) {
      return null;
    }
    return selectedImportFiles.slice(0, 5).map((file) => file.name).join(", ");
  }, [selectedImportFiles]);

  const handleImportDialogClose = useCallback(() => {
    if (importingAnnotations) {
      return;
    }
    resetImportDialog();
  }, [importingAnnotations, resetImportDialog]);

  const handleCreateAnnotation = useCallback(
    async (input: AnnotationUpsertInput) => {
      const normalizedInput: AnnotationUpsertInput = {
        ...input,
        restricted: hasAdminRights ? input.restricted : false
      };
      const targetStorage: AnnotationStorage = normalizedInput.storage;

      if (targetStorage === "local") {
        updateLocalAnnotations((current) => [
          ...current,
          {
            ...normalizedInput,
            id: createLocalAnnotationId(),
            storage: "local"
          }
        ]);
      } else {
        await createServerAnnotation({ ...normalizedInput, storage: "server" });
        await invalidateAnnotations();
      }

      setSnackbar({ open: true, message: `${normalizedInput.name} was added.`, severity: "success" });
      setSelectedAnnotationIds([]);
    },
    [createServerAnnotation, hasAdminRights, invalidateAnnotations, updateLocalAnnotations]
  );

  const handleUpdateAnnotation = useCallback(
    async (input: AnnotationUpsertInput) => {
      if (!editingAnnotation) {
        throw new Error("Annotation is not selected.");
      }

      if (editingAnnotation.restricted && !hasAdminRights) {
        throw new Error(RESTRICTED_ADMIN_ONLY_MESSAGE);
      }

      const normalizedInput: AnnotationUpsertInput = {
        ...input,
        restricted: hasAdminRights ? input.restricted : editingAnnotation.restricted
      };

      const targetStorage: AnnotationStorage = normalizedInput.storage;

      if (editingAnnotation.storage === "local" && targetStorage === "local") {
        updateLocalAnnotations((current) =>
          current.map((annotation) =>
            annotation.id === editingAnnotation.id
              ? { ...normalizedInput, id: editingAnnotation.id, storage: "local" }
              : annotation
          )
        );
      } else if (editingAnnotation.storage === "local" && targetStorage === "server") {
        await createServerAnnotation({ ...normalizedInput, storage: "server" });
        updateLocalAnnotations((current) => current.filter((annotation) => annotation.id !== editingAnnotation.id));
        await invalidateAnnotations();
      } else if (editingAnnotation.storage === "server" && targetStorage === "local") {
        const localCopy: AnnotationSummary = {
          ...normalizedInput,
          id: createLocalAnnotationId(),
          storage: "local"
        };

        updateLocalAnnotations((current) => [...current, localCopy]);

        try {
          await deleteServerAnnotation(editingAnnotation.id);
          await invalidateAnnotations();
        } catch (err) {
          updateLocalAnnotations((current) => current.filter((annotation) => annotation.id !== localCopy.id));
          throw err;
        }
      } else {
        await updateServerAnnotation(editingAnnotation.id, { ...normalizedInput, storage: "server" });
        await invalidateAnnotations();
      }

      setSnackbar({ open: true, message: `${normalizedInput.name} was updated.`, severity: "success" });
      setSelectedAnnotationIds([]);
    },
    [createServerAnnotation, deleteServerAnnotation, editingAnnotation, hasAdminRights, invalidateAnnotations, updateLocalAnnotations, updateServerAnnotation]
  );

  const handleOpenDelete = (targets: AnnotationSummary[]) => {
    if (!targets.length) {
      return;
    }
    if (!hasAdminRights && targets.some((target) => target.restricted)) {
      setSnackbar({ open: true, message: RESTRICTED_ADMIN_ONLY_MESSAGE, severity: "error" });
      return;
    }
    setDeleteTargets(targets);
    setDeleteError(null);
  };

  const handleCloseDelete = () => {
    setDeleteTargets([]);
    setDeleteError(null);
    setDeletingAnnotation(false);
  };

  const handleConfirmDelete = async () => {
    if (!deleteTargets.length) {
      return;
    }
    setDeletingAnnotation(true);
    setDeleteError(null);

    if (!hasAdminRights && deleteTargets.some((target) => target.restricted)) {
      setDeleteError(RESTRICTED_ADMIN_ONLY_MESSAGE);
      setDeletingAnnotation(false);
      return;
    }

    try {
      const serverTargets = deleteTargets.filter((target) => target.storage === "server");
      const localTargets = deleteTargets.filter((target) => target.storage === "local");

      for (const target of serverTargets) {
        // eslint-disable-next-line no-await-in-loop
        await deleteServerAnnotation(target.id);
      }

      if (localTargets.length > 0) {
        const localIds = new Set(localTargets.map((target) => target.id));
        updateLocalAnnotations((current) => current.filter((annotation) => !localIds.has(annotation.id)));
      }

      setSnackbar({
        open: true,
        message:
          deleteTargets.length === 1
            ? `${deleteTargets[0].name} was removed.`
            : `${deleteTargets.length} annotations were removed.`,
        severity: "success"
      });
      setSelectedAnnotationIds((prev) => prev.filter((id) => !deleteTargets.some((target) => target.id === id)));
      handleCloseDelete();
      if (serverTargets.length > 0) {
        await invalidateAnnotations();
      }
    } catch (err) {
      const message = err instanceof Error ? err.message : "Unable to delete annotation.";
      setDeleteError(message);
      setDeletingAnnotation(false);
    }
  };

  const actionButtons = (
    <Stack direction="row" spacing={0.5} alignItems="center" flexWrap="wrap" justifyContent="flex-end">
      <Tooltip title="Filter by area">
        <ToggleButton
          value="annotation-area-filter"
          selected={Boolean(annotationAreaFilter)}
          onClick={handleAreaFilterToggle}
          color="primary"
          aria-label="Filter by area"
          sx={{ whiteSpace: "nowrap" }}
        >
          <FilterAltIcon sx={{ mr: 1 }} fontSize="small" />
          Filter by area
        </ToggleButton>
      </Tooltip>
      <Button
        variant="outlined"
        startIcon={<AddBoxIcon />}
        onClick={() => setAddOpen(true)}
        sx={{ whiteSpace: "nowrap" }}
      >
        Add annotation
      </Button>
      <Tooltip title="Edit annotation">
        <span>
          <IconButton
            color="primary"
            aria-label="Edit annotation"
            onClick={() => {
              const target = selectedAnnotations[selectedAnnotations.length - 1];
              if (target) {
                setEditingAnnotation(target);
              }
            }}
            disabled={!canEditSelected}
            size="small"
          >
            <EditIcon />
          </IconButton>
        </span>
      </Tooltip>
      <Tooltip title="Delete annotation(s)">
        <span>
          <IconButton
            color="primary"
            aria-label="Delete annotations"
            onClick={() => handleOpenDelete(selectedAnnotations)}
            disabled={!canDeleteSelected}
            size="small"
          >
            <DeleteIcon />
          </IconButton>
        </span>
      </Tooltip>
      <Tooltip title="Export annotations">
        <span>
          <IconButton
            color="primary"
            aria-label="Export annotations"
            onClick={handleExportAnnotations}
            disabled={!canExportSelected}
            size="small"
          >
            <FileDownloadIcon />
          </IconButton>
        </span>
      </Tooltip>
      <Tooltip title="Import annotations">
        <span>
          <IconButton
            color="primary"
            aria-label="Import annotations"
            onClick={handleImportClick}
            size="small"
          >
            <FileUploadIcon />
          </IconButton>
        </span>
      </Tooltip>
    </Stack>
  );

  return (
    <Box>
      <Box
        display="flex"
        flexDirection={{ xs: "column", md: "row" }}
        alignItems={{ xs: "stretch", md: "center" }}
        justifyContent="space-between"
        gap={2}
        mb={2}
      >
        <Box>
          <Box display="inline-flex" alignItems="center" gap={1}>
            <EditNoteIcon sx={{ color: "text.secondary" }} />
            <Typography variant="h5" sx={{ m: 0 }}>
              Annotations
            </Typography>
            <Badge badgeContent={annotations.length} color="primary" sx={{ ml: 1 }} />
          </Box>
        </Box>
        {actionButtons}
      </Box>
      <Stack spacing={2}>
        {isUserManagementEnabled && hasAdminRights && isUserGroupsError ? (
          <Alert severity="error">
            {userGroupsError instanceof Error ? userGroupsError.message : "Unable to load user groups."}
          </Alert>
        ) : null}
        {isLoading ? (
          <Stack alignItems="center" py={4}>
            <CircularProgress />
          </Stack>
        ) : isError ? (
          <Alert severity="error">
            {error instanceof Error ? error.message : "Unable to load annotations."}
          </Alert>
        ) : annotations.length === 0 ? (
          <Alert severity="info">No annotations found.</Alert>
        ) : (
          <AnnotationTable
            annotations={annotations}
            selectedAnnotationIds={selectedAnnotationIds}
            onSelectionChange={setSelectedAnnotationIds}
          />
        )}
      </Stack>
      <AnnotationDialog
        open={addOpen}
        title="Add annotation"
        submitLabel="Add"
        isAdmin={hasAdminRights}
        annotation={null}
        availableUserGroups={availableUserGroups}
        onClose={() => setAddOpen(false)}
        onSubmit={handleCreateAnnotation}
      />
      <AnnotationDialog
        open={Boolean(editingAnnotation)}
        title="Edit annotation"
        submitLabel="Save"
        isAdmin={hasAdminRights}
        annotation={editingAnnotation}
        availableUserGroups={availableUserGroups}
        onClose={() => setEditingAnnotation(null)}
        onSubmit={handleUpdateAnnotation}
      />
      <AnnotationAreaFilterDialog
        open={areaFilterDialogOpen}
        initialBBox={annotationAreaFilter ?? lastSelectedAreaFilterBBox}
        onClose={() => setAreaFilterDialogOpen(false)}
        onSelect={handleAreaFilterSelected}
      />
      <Dialog open={deleteTargets.length > 0} onClose={handleCloseDelete} fullWidth maxWidth="xs">
        <DialogTitle>Delete annotation{deleteTargets.length === 1 ? "" : "s"}</DialogTitle>
        <DialogContent>
          <Stack spacing={2} sx={{ pt: 1 }}>
            {deleteError ? <Alert severity="error">{deleteError}</Alert> : null}
            <Typography>
              {deleteTargets.length === 1
                ? `Remove ${deleteTargets[0].name}?`
                : `Remove ${deleteTargets.length} selected annotations?`}
            </Typography>
          </Stack>
        </DialogContent>
        <DialogActions>
          <Button onClick={handleCloseDelete} disabled={deletingAnnotation}>Cancel</Button>
          <Button color="error" variant="contained" onClick={handleConfirmDelete} disabled={deletingAnnotation}>
            Delete
          </Button>
        </DialogActions>
      </Dialog>
      <Dialog
        open={importDialogOpen}
        onClose={handleImportDialogClose}
        fullWidth
        maxWidth="sm"
      >
        <DialogTitle>Import annotations</DialogTitle>
        <DialogContent dividers>
          <FormControl fullWidth sx={{ mb: 2 }}>
            <InputLabel id="annotation-import-storage-label">Storage</InputLabel>
            <Select
              labelId="annotation-import-storage-label"
              value={importTargetStorage}
              label="Storage"
              onChange={(event) => {
                setImportTargetStorage(event.target.value as AnnotationStorage);
              }}
              disabled={importingAnnotations}
            >
              <MenuItem value="local">Local</MenuItem>
              <MenuItem value="server">Server</MenuItem>
            </Select>
          </FormControl>
          <Box
            onDragOver={handleImportDragOver}
            onDragLeave={handleImportDragLeave}
            onDrop={handleImportDrop}
            onClick={() => fileInputRef.current?.click()}
            sx={{
              border: 2,
              borderStyle: "dashed",
              borderColor: importDragActive ? "primary.main" : importError ? "error.main" : "divider",
              borderRadius: 2,
              p: 4,
              textAlign: "center",
              backgroundColor: importDragActive ? "action.hover" : "background.paper",
              cursor: "pointer",
              transition: "all 0.2s",
              "&:hover": {
                borderColor: "primary.main",
                backgroundColor: "action.hover"
              }
            }}
          >
            <CloudUploadIcon sx={{ fontSize: 48, color: "text.secondary", mb: 2 }} />
            <Typography variant="body1" gutterBottom>
              {importFileSummary}
            </Typography>
            {!hasAdminRights && importTargetStorage === "local" ? (
              <Typography variant="caption" color="text.secondary" sx={{ display: "block", mb: 1 }}>
                Imported annotations will be saved to local storage.
              </Typography>
            ) : null}
            {!hasAdminRights && importTargetStorage === "server" ? (
              <Typography variant="caption" color="text.secondary" sx={{ display: "block", mb: 1 }}>
                Imported annotations will be saved to server storage as unrestricted annotations.
              </Typography>
            ) : null}
            <Typography variant="caption" color="text.secondary" sx={{ display: "block" }}>
              Accepts multiple .json, .geojson, and .gejson files
            </Typography>
            {importFileDetails ? (
              <Typography variant="caption" color="text.secondary" sx={{ display: "block", mt: 1 }}>
                {importFileDetails}
              </Typography>
            ) : null}
          </Box>
          {importError ? (
            <Alert severity="error" sx={{ mt: 2 }}>
              {importError}
            </Alert>
          ) : null}
          {!importError && selectedImportFiles.length > 0 ? (
            <Alert severity="success" sx={{ mt: 2 }}>
              {selectedImportFiles.length === 1
                ? "File ready to import"
                : `${selectedImportFiles.length} files ready to import`}
            </Alert>
          ) : null}
        </DialogContent>
        <DialogActions>
          <Button onClick={handleImportDialogClose} disabled={importingAnnotations}>Cancel</Button>
          <Button
            onClick={handleConfirmImport}
            variant="contained"
            startIcon={<FileUploadIcon />}
            disabled={selectedImportFiles.length === 0 || importingAnnotations}
          >
            Import
          </Button>
        </DialogActions>
      </Dialog>
      <Snackbar
        open={snackbar.open}
        autoHideDuration={4000}
        onClose={closeSnackbar}
        message={snackbar.message}
      />
      <input
        ref={fileInputRef}
        type="file"
        accept=".json,.geojson,.gejson,application/json,application/geo+json"
        multiple
        hidden
        onChange={handleImportFileChange}
      />
    </Box>
  );
};

export default AnnotationsPage;
